import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import Link from 'corp/components/UI/IsomorphicLink/IsomorphicLink';
import { IS_FEDERAL } from '@scale/frontend-lib/constants';
import React from 'react';
import _ from 'lodash';
import moment from 'moment';

import RemotasksExternalLink from 'corp/components/UI/RemotasksExternalLink';
import { Button as OldButton } from 'design-system/Form';
import { Button } from 'design-system';
import HideableSection from 'corp/components/UI/HideableSection';
import ObjectDetails from './sections/ObjectDetails';
import CorpAliases from './sections/CorpAliases';
import { isLidarOrSensorFusion, lidarTypes, PipelineStageStatus } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import LookupHeader from './LookupHeader';
import { Extras } from './LookupTypes';
import { CorpLookupScratchpad } from './CorpLookupScratchpad';
import Tooltip from 'design-system/Tooltip';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import SAlert from 'react-s-alert';
import CorpButtons from './CorpButtons';
import NewSlackChannelEditor from 'corp/components/SlackChannel/NewSlackChannelEditor';
import { TaskPipelineObject } from '@scale/shared/pipelinev3/lookup';
import RemoAdminContext from 'react-scale/components/remoAdmin/remoAdminContext';
import { remoAdminBenchmarkLink } from '../QM/QMBenchmarksTable/QMBenchmarksTable';
import { RemoAdminLink } from 'react-scale/components/remoAdmin/remoAdminLookupLink';
import { AuditPlusFixButton } from './AuditPlusFixButton';
import { CorpSound } from '@scale/shared/sounds';
import OpenChatBenchmarkModal, {
  BenchmarkPreviewType,
} from 'corp/components/Benchmarks/CreateBenchmark/OpenChatBenchmarkModal';
import { getLidarLiteLink, getLidarLitePath } from 'react-scale/lib/helpers/redirectHelpers';
import { getBenchmarkOpsHubLink } from 'react-scale/corp/views/LLMControlCenter/BenchmarkCenterPage/utils';
import SLALookupObject from './SLALookupObject';

/* eslint-disable complexity */

interface SharedHeaderSectionsProps {
  object: $FIXME;
  extras?: Extras;
  refresh: () => void;
  shortcuts?: React.ReactNode;
  hideShortcuts?: boolean;
  hideJson?: boolean;
}

interface SharedHeaderSectionsState {
  openBenchmarksModal?: BenchmarkPreviewType;
}

export class SharedHeaderSections extends React.Component<
  SharedHeaderSectionsProps,
  SharedHeaderSectionsState
> {
  constructor(props: SharedHeaderSectionsProps) {
    super(props);
    this.state = {};
  }

  render() {
    const { object } = this.props;
    const scratchPadUrl = this.props.extras?.corpLookupScratchPad;
    return (
      <React.Fragment>
        <LookupHeader object={object} />
        <HideableSection name="Overview">{this.renderOverview(object)}</HideableSection>
        {object && object.__t === 'taskpermissiongroup' && (
          <HideableSection name="Associated Slack Channels">
            {this.renderSlackChannels(object)}
          </HideableSection>
        )}
        <HideableSection name="Scratchpad">
          {scratchPadUrl && <CorpLookupScratchpad url={scratchPadUrl} />}
        </HideableSection>
      </React.Fragment>
    );
  }

  renderSlackChannels(object: $FIXME) {
    if (object.__t === 'taskpermissiongroup') {
      const projects = object.projects || [];
      return (
        <div>
          {(projects || []).map((project: { [key: string]: any }) => (
            <NewSlackChannelEditor
              key={project._id}
              projectId={project._id}
              projectName={project.name}
              isEditable={true}
              showTitle={true}
              onChannelsCreated={() => {}}
            />
          ))}
        </div>
      );
    } else {
      return <></>;
    }
  }

  renderOverview(object: $FIXME) {
    const type = object && object.__t;
    if (type === 'script') return;
    return (
      <div className="lookup-overview">
        <div className="lookup-object-details">
          <ObjectDetails {...object} />
        </div>
        {this.renderShortcuts()}
        <style jsx>{`
          .lookup-overview {
            display: flex;
          }

          .lookup-overview > div {
            flex-basis: 50%;
          }
          .lookup-object-details {
            max-width: 700px;
            padding-right: 10px;
          }
        `}</style>
      </div>
    );
  }

  renderShortcuts() {
    if (this.props.hideShortcuts) {
      return null;
    }
    if (this.props.shortcuts !== undefined) {
      return this.props.shortcuts;
    }
    const { object } = this.props;
    return this.renderShortcutsForType(object);
  }

  renderShortcutsForType(object: $FIXME) {
    if (_.includes(['taskattempt', 'taskreview'], object.__t)) {
      return (
        <div className="shortcuts">
          {object.fullstorySession && (
            <OldButton textTransform="inherit" href={object.fullstorySession}>
              FullStory session
            </OldButton>
          )}

          <div className="hsp__2" />
        </div>
      );
    } else if (object.__t === 'testtask') {
      const isLidar = _.includes(lidarTypes, object.type);
      const id = object.id;
      return (
        <div>
          {isLidar && (
            <a
              href={getLidarLiteLink({
                entrypoint: 'debug',
                taskId: id,
              })}
            >
              Debug in Lidar Lite
            </a>
          )}
        </div>
      );
    } else if (object.__t === 'trainingscenario') {
      const id = object._id;
      const project = object.project;
      // object.params.binaryScene check is temporary until all lidar scenarios are migrated to lidar lite
      const isLidarLiteScenario =
        isLidarOrSensorFusion(object.taskType) && object.params.binarySceneUrl;
      const isChat =
        object.taskType === TaskType.Chat ||
        object.taskType === TaskType.ChatExperimental ||
        object.taskType === TaskType.ChatV2;
      const isMagicBenchmark =
        _.get(object, 'metadata.chatBenchmarkConfig.corruptionConfig.eligibleCorruptions', [])
          .length > 0;
      const isTextCollection = object.taskType === TaskType.TextCollection;
      return (
        <RemoAdminContext.Consumer>
          {({ isRemoAdmin }) => (
            <div>
              <div>
                {!isChat ? (
                  <RemotasksExternalLink
                    path={
                      isLidarLiteScenario
                        ? `/lidarlite/?trainingId=${id}`
                        : `/training?trainingId=${id}`
                    }
                  >
                    Open Training
                  </RemotasksExternalLink>
                ) : isMagicBenchmark ? (
                  <></>
                ) : (
                  <a
                    onClick={() => {
                      this.setState({ openBenchmarksModal: BenchmarkPreviewType.Training });
                    }}
                  >
                    Open Scenario in Course View (Training)
                  </a>
                )}
              </div>
              <div>
                {!isChat ? (
                  <RemotasksExternalLink
                    path={
                      isLidarLiteScenario
                        ? `/lidarlite/?trainingId=${id}`
                        : `/tasks?scenarioId=${id}`
                    }
                  >
                    Open Scenario in Queue
                  </RemotasksExternalLink>
                ) : (
                  <a
                    onClick={() => {
                      this.setState({ openBenchmarksModal: BenchmarkPreviewType.Tasks });
                    }}
                  >
                    Open Scenario in Queue
                  </a>
                )}
              </div>
              {!!this.state.openBenchmarksModal && (
                <OpenChatBenchmarkModal
                  open={!!this.state.openBenchmarksModal}
                  onClose={() => {
                    this.setState({ openBenchmarksModal: undefined });
                  }}
                  scenarioId={id}
                  previewType={this.state.openBenchmarksModal}
                />
              )}
              {object.taskType === TaskType.SensorFusion ||
              object.taskType === TaskType.DataCollectionV2 ? (
                <RemotasksExternalLink
                  path={getLidarLitePath(`entrypoint=scenario-editor&scenarioId=${id}`)}
                >
                  Edit Scenario in Lidarlite
                </RemotasksExternalLink>
              ) : (
                <div>
                  {isRemoAdmin ? (
                    remoAdminBenchmarkLink(id, 'Edit Scenario')
                  ) : (
                    <Link href={`/edit_scenario?scenarioId=${id}`}>Edit Scenario</Link>
                  )}
                </div>
              )}
              {object.taskType === TaskType.Categorization && (
                <div>
                  <Link href={`/benchmarks/parameterize?project=${project}&scenario=${id}`}>
                    <a target="_blank">Edit Benchmark Parameters</a>
                  </Link>
                </div>
              )}
              {(isChat || isTextCollection) && (
                <div style={{ marginTop: '15px' }}>
                  <Link href={getBenchmarkOpsHubLink(project._id, id)} target="_blank">
                    View in GenAI Ops Hub
                  </Link>
                </div>
              )}
            </div>
          )}
        </RemoAdminContext.Consumer>
      );
    } else if (object.__t === 'trainingattempt') {
      const type = object && object.__t;
      const corpButtons =
        type === 'trainingattempt'
          ? [
              {
                commandName: 'setAttemptResponseAsScenarioResponse',
                text: 'Use as scenario response',
                tooltip:
                  "Use this if the current response is more accurate than the training scenario's response",
              },
              {
                commandName: 'regradeTrainingAttempt',
                text: 'Regrade and update TME',
                tooltip:
                  "Regrade this training attempt with the current project's grader configs AND update TaskerMetricsEntry",
                args: {
                  updateMetrics: true,
                },
              },
            ]
          : [];
      return (
        <div>
          <CorpButtons objectId={object._id} buttons={corpButtons} refresh={this.props.refresh} />
        </div>
      );
    } else if (object.__t === 'servicelevelagreement') {
      return <SLALookupObject object={object} />;
    } else if (object.__t === 'course') {
      return (
        <RemoAdminContext.Consumer>
          {({ isRemoAdmin }) => (
            <div>
              <div>
                {isRemoAdmin ? (
                  <RemoAdminLink
                    displayValue={'Edit Course'}
                    href={`/remoadmin/tools/courses/${object._id}`}
                  />
                ) : (
                  <Link
                    href={
                      object.courseType === 'native'
                        ? `/genai-ops-hub/course-library/${object._id}`
                        : `/courses/${object._id}`
                    }
                  >
                    Link to Course Page
                  </Link>
                )}
              </div>
              <div>
                <RemotasksExternalLink path={`/course?id=${object._id}`}>
                  Open Course in Remotasks
                </RemotasksExternalLink>
              </div>
              <div>
                <RemotasksExternalLink path={`/course?id=${object._id}`} isOutlier={true}>
                  Open Course in Outlier
                </RemotasksExternalLink>
              </div>
            </div>
          )}
        </RemoAdminContext.Consumer>
      );
    } else if (object.__t === 'ontology') {
      return (
        <div>
          <Link href={`/ontology/${object._id}`}>Link to Ontology Editor</Link>
        </div>
      );
    } else if (object.__t === 'auditbatch') {
      return (
        <div>
          <div>
            <Link href={`/speed_audit/${object._id}`}>Link to Speed Audit</Link>
          </div>
          <div>
            <Link href={`/v2/speed_audit/${object._id}`}>Link to Speed Audit v2</Link>
          </div>
        </div>
      );
    } else if (object.__t === 'pipelineStage') {
      const reactivateValidStatuses = new Set([
        PipelineStageStatus.Inactive,
        PipelineStageStatus.Failed,
        PipelineStageStatus.ActivationError,
        PipelineStageStatus.CompletionError,
      ]);
      if (object.nodeState.nodeType === 'step_fn') {
        reactivateValidStatuses.add(PipelineStageStatus.Active);
      }
      const pipelineStageShortcuts = [
        {
          command: 'reactivatePipelineStage',
          description: 'Reactivate',
          enabled: reactivateValidStatuses.has(object.status),
          tooltip: 'Rerun this pipeline stage from scratch, including activation code',
          alertSound: CorpSound.Reactivate,
        },
        {
          command: 'retryCompletionPipelineStage',
          description: 'Retry Completion',
          enabled:
            object.status === PipelineStageStatus.CompletionError ||
            (object.status === PipelineStageStatus.CompletionStarted &&
              moment(object.completionStartedAt).isBefore(moment().utc().subtract(2, 'minutes'))),
          tooltip: 'Retry running completion code for this pipeline',
        },
      ];

      return (
        <div>
          {pipelineStageShortcuts.map(
            ({ command, description, enabled, tooltip, alertSound }, i) => (
              <PipelineStageShortcut
                key={i}
                command={command}
                description={description}
                enabled={enabled}
                tooltip={tooltip}
                alertSound={alertSound}
                objectId={object._id}
                refresh={this.props.refresh}
              />
            ),
          )}
        </div>
      );
    } else if (object.__t === 'auditplus') {
      return (
        <div style={{ marginBottom: '10px' }}>
          <AuditPlusFixButton projectId={object.project} auditPlusIds={[object._id]} />
        </div>
      );
    } else {
      return null;
    }
  }
}

interface IPipelineStageShortcutProps {
  command: string;
  description: string;
  enabled: boolean;
  tooltip: string;
  alertSound?: string;

  objectId: string;

  refresh: () => void;
}

function PipelineStageShortcut(props: IPipelineStageShortcutProps) {
  return (
    <Tooltip key={props.command} title={props.tooltip} placement="right" arrow>
      <div style={{ marginBottom: '10px' }}>
        <Button
          type="error"
          disabled={!props.enabled}
          onClick={async () => {
            if (!window.confirm('Are you sure you want to do this?')) {
              return;
            }

            try {
              await apiFetch(
                null,
                `corp-api/lookup/${props.objectId}/runCommand/${props.command}`,
                {
                  method: 'POST',
                },
              );
              props.refresh();
            } catch (error) {
              defaultApiFetchErrorHandler(error, { handleHttp: true });
            }
          }}
        >
          {props.description}
        </Button>
      </div>
    </Tooltip>
  );
}

interface SharedFooterSectionsProps {
  object: $FIXME;
  hideJson?: boolean;
  rawObject?: $FIXME;
}

interface SharedFooterSectionsState {
  aliases: $FIXME[];
}

export class SharedFooterSections extends React.Component<
  SharedFooterSectionsProps,
  SharedFooterSectionsState
> {
  constructor(props: SharedFooterSectionsProps) {
    super(props);

    this.state = {
      aliases: [],
    };
  }

  async componentDidMount() {
    // XXX This logic should be moved to the container's fetchExtras function
    try {
      const aliases = await apiFetch(null, `corp-api/lookup/${this.props.object._id}/aliases`);
      this.setState({ aliases });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Could not fetch aliases');
    }
  }

  render() {
    const { object } = this.props;

    const openSelectedIfUrl = ({ name, value }: OnSelectProps) => {
      if (name && value && typeof value === 'string' && name.toLowerCase().includes('url')) {
        try {
          const url = new URL(value);
          const newTab = window.open(url, '_blank', 'noopener,noreferrer');
          if (newTab) {
            newTab.opener = null;
          }
        } catch (err) {
          SAlert.error(err);
        }
      }
    };

    return (
      <React.Fragment>
        <RemoAdminContext.Consumer>
          {remoAdminContext => (
            <>
              {!this.props.hideJson &&
                !remoAdminContext.hidePII &&
                (object.__t === 'taskattempt' ||
                  object.__t === 'subtask' ||
                  object.__t === TaskPipelineObject.TaskPipelineNode) && (
                  <HideableSection name="Claim History" forceHidden={true}>
                    {this.renderClaimHistory(object)}
                  </HideableSection>
                )}

              {!this.props.hideJson && object.__t === 'corpjob' && object.jsonOutput && (
                <HideableSection name="Corp Job Output">
                  <ReactJsonView
                    // @ts-ignore
                    src={object.jsonOutput}
                    name={false}
                    theme="monokai"
                    iconStyle="triangle"
                    collapsed={1}
                    collapseStringsAfterLength={120}
                    groupArraysAfterLength={false}
                    enableClipboard={true}
                    displayObjectSize={false}
                    displayDataTypes={false}
                  />
                </HideableSection>
              )}

              {!this.props.hideJson && object.__t === 'corpjob' && object.jsonInput && (
                <HideableSection name="Corp Job Input">
                  <ReactJsonView
                    // @ts-ignore
                    src={object.jsonInput}
                    name={false}
                    theme="monokai"
                    iconStyle="triangle"
                    collapsed={1}
                    collapseStringsAfterLength={120}
                    groupArraysAfterLength={false}
                    enableClipboard={true}
                    displayObjectSize={false}
                    displayDataTypes={false}
                  />
                </HideableSection>
              )}

              {!this.props.hideJson &&
                object.__t === TaskPipelineObject.TaskPipelineResource &&
                object.value && (
                  <HideableSection name="Task Pipeline Resource Value">
                    <ReactJsonView
                      // @ts-ignore
                      src={object.value}
                      name={false}
                      theme="monokai"
                      iconStyle="triangle"
                      collapsed={1}
                      collapseStringsAfterLength={120}
                      groupArraysAfterLength={false}
                      enableClipboard={true}
                      displayObjectSize={false}
                      displayDataTypes={false}
                    />
                  </HideableSection>
                )}

              {!this.props.hideJson &&
                object.__t === TaskPipelineObject.TaskPipelineNode &&
                object.executionMeta && (
                  <HideableSection name="Task Pipeline Node Execution Meta">
                    <ReactJsonView
                      // @ts-ignore
                      src={object.executionMeta}
                      name={false}
                      theme="monokai"
                      iconStyle="triangle"
                      collapsed={1}
                      collapseStringsAfterLength={120}
                      groupArraysAfterLength={false}
                      enableClipboard={true}
                      displayObjectSize={false}
                      displayDataTypes={false}
                    />
                  </HideableSection>
                )}

              {!this.props.hideJson &&
                object.__t === TaskPipelineObject.TaskPipelineNode &&
                (object.input ||
                  object.output ||
                  object.inputResources ||
                  object.outputResources) && (
                  <HideableSection name="Input and Output Values">
                    <ReactJsonView
                      src={{
                        input: object.input ?? {},
                        inputResources: object.inputResources ?? {},
                        output: object.output ?? {},
                        outputResources: object.outputResources ?? {},
                      }}
                      name={false}
                      theme="monokai"
                      iconStyle="triangle"
                      collapsed={1}
                      collapseStringsAfterLength={120}
                      groupArraysAfterLength={false}
                      enableClipboard={true}
                      displayObjectSize={false}
                      displayDataTypes={false}
                    />
                  </HideableSection>
                )}

              {!this.props.hideJson && !remoAdminContext.hideCustomer && (
                <HideableSection name="Original JSON">
                  <ReactJsonView
                    src={this.props.rawObject || object}
                    name={false}
                    theme="monokai"
                    iconStyle="triangle"
                    collapsed={1}
                    onSelect={openSelectedIfUrl}
                    collapseStringsAfterLength={120}
                    groupArraysAfterLength={false}
                    enableClipboard={true}
                    displayObjectSize={false}
                    displayDataTypes={false}
                  />
                </HideableSection>
              )}

              {!this.props.hideJson && !IS_FEDERAL && !remoAdminContext.hideCustomer && (
                <HideableSection name="Oplog" forceHidden={true}>
                  {this.renderOplog(object)}
                </HideableSection>
              )}

              {object._id.toString().match(/[0-9a-f]{24}/) && (
                <HideableSection name="Aliases" forceHidden={true}>
                  <CorpAliases objectId={object._id.toString()} />
                </HideableSection>
              )}
            </>
          )}
        </RemoAdminContext.Consumer>
      </React.Fragment>
    );
  }

  renderOplog(object: $FIXME) {
    const id = object._id.toString();
    return (
      <iframe
        src={
          'https://redash.scale.com/embed/query/1816/visualization/2725?&hide_parameters=1&maxage=300&p_oid=' +
          id
        }
        width="100%"
        height="500"
        frameBorder="0"
      ></iframe>
    );
  }

  renderClaimHistory(object: $FIXME) {
    if (object.__t === TaskPipelineObject.TaskPipelineNode) {
      if (!object.pipelineV3HumanNode) {
        return null;
      }
      return (
        <iframe
          src={
            'https://redash.scale.com/embed/query/117211/visualization/210944?p_oid=' +
            object.pipelineV3HumanNode._id
          }
          width="100%"
          height="500"
          frameBorder="0"
        ></iframe>
      );
    }

    const id = object._id.toString();
    return (
      <iframe
        src={
          'https://redash.scale.com/embed/query/1817/visualization/2726?hide_parameters=1&max_age=300&p_oid=' +
          id
        }
        width="100%"
        height="500"
        frameBorder="0"
      ></iframe>
    );
  }
}

export function shouldRenderLookupTaskPreview({ object }: { object: $FIXME }) {
  if (!object) {
    return false;
  }

  return _.includes(
    [
      'subtask',
      'task',
      'taskattempt',
      'taskreview',
      'reviewerconfidence',
      'testtask',
      'trainingattempt',
    ],
    object.__t,
  );
}

export function getLookupCustomerAuditHistoryProps({
  object,
  scalerAccess,
}: {
  object: $FIXME;
  scalerAccess: $FIXME;
}) {
  if (!object) {
    return null;
  }
  if (!_.includes(['subtask', 'task', 'customeraudit'], object.__t)) {
    return null;
  }

  const isSubtask = object.__t === 'subtask';

  const taskType = object.type || object.taskType;
  const subtaskId =
    (object.__t === 'subtask' && object._id) ||
    (object.__t === 'task' && object.subtasks[0]?._id) ||
    (object.__t === 'customeraudit' && object.subtask && object.subtask._id) ||
    object.subtask ||
    object.task?.subtasks?.[0]?._id ||
    object.task?.subtasks?.[0];

  let customerAudits =
    (isSubtask
      ? object?.customerAudits?.filter(
          (audit: $FIXME) => audit.subtask === subtaskId || audit.subtask == null,
        )
      : object?.customerAudits) ?? [];
  customerAudits = customerAudits.filter((audit: $FIXME) => !audit.isInternalAudit);

  if (!customerAudits || customerAudits.length === 0) {
    return null;
  }

  return {
    audits: customerAudits,
    scalerAccess,
    isLidar: taskType === TaskType.LidarAnnotation,
    isDocumentModel: taskType === TaskType.DocumentModel,
    subtaskId,
  };
}

export function getLookupInternalAuditHistoryProps({ object }: { object: $FIXME }) {
  if (!object) {
    return null;
  }
  if (!_.includes(['task', 'customeraudit'], object.__t)) {
    return null;
  }

  const internalAudits = object.internalAudits;

  if (!internalAudits || internalAudits.length === 0) {
    return null;
  }
  return {
    audits: internalAudits,
  };
}
